import { createContext, useContext, useMemo, type ReactNode } from 'react'

import axios, { type AxiosInstance, type AxiosResponse, AxiosError, type CreateAxiosDefaults } from 'axios'

import { ApiError, ErrorHandler, ExtendedApiError } from '@/types/api.types'

import AppService from '@/services/app.service'

import { NavigationService } from '@/providers/NavigationProvider'
import AppRoutes from '@/config/app.routes'
import { useAxiosInterceptors } from '@/hooks/UseAxiosInterceptors'

NavigationService.setBaseUrl(AppService.getAppBaseURL() || "")

interface ApiClientConfig {
  baseURL?: string
  timeout?: number
  retryAttempts?: number
  retryDelay?: number
}

/*
class ApiClientService {
  private static instance: ApiClientService
  private readonly config: ApiClientConfig
  private readonly axiosInstance: AxiosInstance
  private errorHandler?: ErrorHandler

  private constructor(config: ApiClientConfig = {}) {
    this.config = {
      baseURL: AppService.getEnv() === 'production' ? AppService.getApiBaseURL() : (import.meta.env.VITE_API_URL || ''),
      timeout: AppService.getApiTimeout() || 10000,
      retryAttempts: 3,
      retryDelay: 1000,
      ...config,
    }

    const axiosDefaults: CreateAxiosDefaults = {
      baseURL: this.config.baseURL,
      timeout: this.config.timeout,
      withCredentials: true,
      headers: {
        "ngrok-skip-browser-warning": "none",
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    }

    this.axiosInstance = axios.create(axiosDefaults)

    this.setupInterceptors()
  }

  public static getInstance(config?: ApiClientConfig): ApiClientService {
    if (!ApiClientService.instance) {
      ApiClientService.instance = new ApiClientService(config)
    }
    return ApiClientService.instance
  }

  public setErrorHandler(handler: ErrorHandler): void {
    this.errorHandler = handler
  }


  private setupInterceptors(): void {

    this.axiosInstance.interceptors.response.use(
      (res) => {
        if (res.data?.redirectTo) {
          NavigationService.navigate(res.data.redirectTo)
        }
        return res
      },
      async (err: ExtendedApiError) => {
        if (err.response?.data?.redirectTo) {
          NavigationService.navigate(err.response.data.redirectTo)
        }

        if (err.response?.status === 302) {
          return this.handleRedirect(err)
        }

        const originalRequest = err.config

        if (
          err.response?.status === 401 &&
          originalRequest &&
          !originalRequest._retry
        ) {
          originalRequest._retry = true

          try {
            console.log("Refreshing access token")
            //const { accessToken } = await this.apiClient.refreshToken()
            //AuthService.setLocalAccessToken(accessToken)
            //originalRequest.headers.Authorization = accessToken
            //  ? `Bearer ${accessToken}`
            //  : ""

            return this.axiosInstance(originalRequest)
          } catch (_error) {
            this.handleLogout()
            if (axios.isAxiosError(_error)) {
              const axiosError = _error as AxiosError<ApiError>
              throw new Error(axiosError.response?.data.message ?? "Unknown error"
              )
            }
            throw _error
          }
        }

        if (this.errorHandler && originalRequest && !originalRequest._retry) {
          if (err.response?.status === 401) {
            this.errorHandler(err, { 
              customMessage: 'Your session has expired. Please log in again.',
              toastType: 'warning'
            })
            this.handleLogout()
          } else if (err.response?.status === 403) {
            if (err.response.data) {
              throw err.response.data
            }
            this.errorHandler(err, { 
              customMessage: 'Access denied. You don\'t have permission for this action.',
              toastType: 'error'
            })
          } else if (err.response?.status && err.response.status >= 500) {
            this.errorHandler(err, { 
              customMessage: 'Server error occurred. Please try again later.',
              toastType: 'error'
            })
          } else if (!err.response && err.code === 'ECONNABORTED') {
            this.errorHandler(err, { 
              customMessage: 'Request timeout. Please check your connection.',
              toastType: 'warning'
            })
          } else if (!err.response) {
            this.errorHandler(err, { 
              customMessage: 'Network error. Please check your internet connection.',
              toastType: 'error'
            })
          }
        }

        throw err
      }
    )
  }

  private handleLogout(): void {
    delete this.axiosInstance.defaults.headers.common['Authorization']
    // AuthService.clearAuthTokens()
    NavigationService.navigate(AppRoutes.LOGIN, { replace: true })
  }

  public getNavigationService() {
    return NavigationService
  }

  public setGlobalRedirectHandler(handler: (url: string) => void): void {
    NavigationService.setGlobalRedirectHandler(handler)
  }

  public get apiClient(): AxiosInstance {
    return this.axiosInstance
  }

}
*/
interface ApiClientContextValue {
  client: AxiosInstance
}

const ApiClientContext = createContext<ApiClientContextValue | null>(null)

interface ApiClientProviderProps {
  children: ReactNode
  config?: ApiClientConfig
  baseURL?: string
  mockClient?: AxiosInstance
}

export const ApiClientProvider = ({
  children,
  config,
  baseURL,
  mockClient,
}: ApiClientProviderProps) => {
  const client = useMemo(() => {
    if (mockClient) {
      return mockClient
    }

    const axiosDefaults: CreateAxiosDefaults = {
      baseURL: AppService.getApiBaseURL() ?? baseURL ?? import.meta.env.VITE_API_URL ?? '',
      timeout: AppService.getApiTimeout() || 10000,
      withCredentials: true,
      headers: {
        "ngrok-skip-browser-warning": "none",
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    }

    return axios.create(axiosDefaults)
  }, [mockClient, baseURL])

  useAxiosInterceptors(client)

  const value = useMemo(() => ({ client }), [client])

  return (
    <ApiClientContext.Provider value={ value }>
      {children}
    </ApiClientContext.Provider>
  )
}

export const useApiClient = (): ApiClientContextValue => {
  const context = useContext(ApiClientContext)
  if (!context) {
    throw new Error('useApiClient must be used within <ApiClientProvider>')
  }
  return context
}

// Export the service instance for direct usage (backward compatibility) TODO: [MVP] Remove
// const ApiService = {
//   NavigationService,
//   setGlobalRedirectHandler: (handler: (url: string) => void) => {
//     ApiClientService.getInstance().setGlobalRedirectHandler(handler)
//   },
// }
